/**
 * Admin: Panic Alerts Panel
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, CheckCircle, MapPin, Clock } from 'lucide-react';

function timeAgo(ts) {
  if (!ts) return 'N/A';
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (diff < 60) return `hace ${diff}s`;
  if (diff < 3600) return `hace ${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h`;
  return new Date(ts).toLocaleString('es-MX');
}

export default function AlertsPanel() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAlerts = async () => {
    try {
      const res = await axios.get('/api/map/panic-alerts');
      setAlerts(res.data);
    } catch (err) {
      console.error('Alerts fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 15000);
    return () => clearInterval(interval);
  }, []);

  const resolveAlert = async (id) => {
    try {
      await axios.patch(`/api/map/panic-alerts/${id}/resolve`);
      setAlerts(prev => prev.map(a => a.id === id ? { ...a, resolvedAt: new Date().toISOString() } : a));
    } catch (_) {}
  };

  const active = alerts.filter(a => !a.resolvedAt);
  const resolved = alerts.filter(a => a.resolvedAt);

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,68,68,0.12)', border: '1px solid rgba(255,68,68,0.25)' }}>
          <AlertTriangle size={18} style={{ color: '#FF3B30' }} />
        </div>
        <div>
          <h1 className="text-xl font-bold text-n-800">Alertas de Pánico</h1>
          <p className="text-sm text-n-500">
            {active.length} activa{active.length !== 1 ? 's' : ''} · {resolved.length} resuelta{resolved.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={fetchAlerts}
          className="ml-auto text-xs px-3 py-1.5 rounded-lg transition-all"
          style={{ background: 'rgba(0,0,0,0.05)', border: '1px solid rgba(0,122,255,0.15)', color: '#007AFF' }}
        >
          Actualizar
        </button>
      </div>

      {loading && (
        <div className="text-center text-n-500 py-12">Cargando alertas...</div>
      )}

      {!loading && alerts.length === 0 && (
        <div className="text-center py-12">
          <CheckCircle size={48} className="mx-auto mb-4 text-n-400" />
          <p className="text-n-500">Sin alertas registradas</p>
        </div>
      )}

      {/* Active alerts */}
      {active.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-s-red mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse inline-block" />
            ALERTAS ACTIVAS ({active.length})
          </h2>
          <div className="space-y-3">
            {active.map(alert => (
              <AlertCard key={alert.id} alert={alert} onResolve={resolveAlert} />
            ))}
          </div>
        </div>
      )}

      {/* Resolved alerts */}
      {resolved.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-n-500 mb-3">RESUELTAS ({resolved.length})</h2>
          <div className="space-y-2">
            {resolved.map(alert => (
              <AlertCard key={alert.id} alert={alert} resolved />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AlertCard({ alert, onResolve, resolved = false }) {
  const username = alert.user?.username || alert.userId;
  const avatarColor = alert.user?.avatarColor || '#FF3B30';

  return (
    <div
      className="rounded-xl p-4"
      style={{
        background: resolved ? 'rgba(15,20,40,0.6)' : 'rgba(255,32,32,0.06)',
        border: `1px solid ${resolved ? 'rgba(255,255,255,0.06)' : 'rgba(255,32,32,0.25)'}`,
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
          style={{ background: avatarColor, color: '#050810' }}
        >
          {(username?.[0] || '?').toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-n-800">{username}</span>
            {!resolved && (
              <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                style={{ background: 'rgba(255,32,32,0.2)', color: '#FF3B30', border: '1px solid rgba(255,32,32,0.3)' }}>
                🚨 ACTIVA
              </span>
            )}
            {resolved && (
              <span className="text-xs px-2 py-0.5 rounded-full"
                style={{ background: 'rgba(74,222,128,0.1)', color: '#4ade80', border: '1px solid rgba(74,222,128,0.2)' }}>
                ✓ Resuelta
              </span>
            )}
          </div>
          <div className="text-sm text-n-600 mt-0.5">{alert.message || 'ALERTA DE PÁNICO'}</div>

          <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-n-500">
            <span className="flex items-center gap-1">
              <Clock size={10} />
              {timeAgo(alert.createdAt)}
            </span>
            {alert.lat && alert.lng && (
              <span className="flex items-center gap-1">
                <MapPin size={10} />
                {alert.lat.toFixed(5)}, {alert.lng.toFixed(5)}
              </span>
            )}
            {alert.channelId && (
              <span>Canal: {alert.channelId.slice(0, 8)}...</span>
            )}
          </div>
        </div>

        {!resolved && onResolve && (
          <button
            onClick={() => onResolve(alert.id)}
            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
            style={{ background: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.25)', color: '#4ade80' }}
          >
            <CheckCircle size={12} />
            Resolver
          </button>
        )}
      </div>
    </div>
  );
}
